import React, { useEffect, useRef, useState } from "react";
import {
  AppleLogoIcon,
  DeviceMobileIcon,
  BellIcon,
  LightningIcon,
} from "@phosphor-icons/react";
import GooglePlayColorIcon from "@/components/GooglePlayColorIcon";
import { BidoraMark } from "@/components/BidoraLogo";

const IOS_ENABLED = process.env.REACT_APP_IOS_ENABLED === "true";
const APP_STORE_URL =
  process.env.REACT_APP_APP_STORE_URL ||
  "https://apps.apple.com/app/bidorah/id0000000000";
const PLAY_STORE_URL = process.env.REACT_APP_PLAY_STORE_URL || "#";

/**
 * Detect the user's OS so we can show only the relevant store badge.
 * - iOS  → show App Store only
 * - Android → show Google Play only
 * - Desktop / unknown → show both
 *
 * Prefers the modern `userAgentData` (Chromium) and falls back to UA-string parsing.
 * Returns "desktop" during SSR / first paint to avoid layout flash on slow detection.
 */
function detectOS() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return "desktop";
  // Inside our Capacitor shell? They're already in the app — desktop layout still fine
  // here because this section is just a marketing pitch. Skip badges entirely.
  if (window.Capacitor && window.Capacitor.isNativePlatform?.()) return "native";
  const uaData = navigator.userAgentData;
  if (uaData?.platform) {
    const p = uaData.platform.toLowerCase();
    if (p.includes("android")) return "android";
    if (p.includes("ios") || p.includes("iphone") || p.includes("ipad")) return "ios";
  }
  const ua = navigator.userAgent || "";
  if (/android/i.test(ua)) return "android";
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "ios";
  // iPadOS 13+ pretends to be Mac — sniff touch points to disambiguate.
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return "ios";
  return "desktop";
}

export default function GetTheAppSection() {
  const videoRef = useRef(null);
  const [os, setOs] = useState("desktop");

  useEffect(() => {
    setOs(detectOS());
  }, []);

  const showPlay = os === "android" || os === "desktop";
  const showAppStore = os === "ios" || os === "desktop";

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    // Safari + some Chromium headless modes refuse to auto-play until we
    // explicitly .play() the element. Harmless no-op otherwise.
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    v.addEventListener("canplay", tryPlay);
    return () => v.removeEventListener("canplay", tryPlay);
  }, []);

  return (
    <section
      className="bg-white border-y border-zinc-200"
      data-testid="home-get-the-app"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
        {/* Left column: copy + CTAs */}
        <div className="order-2 lg:order-1">
          <p className="text-sm font-semibold text-[#1D78AB] mb-3">
            Bidorah · Mobile
          </p>
          <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter leading-[0.98]">
            Get the app. Never miss a drop.
          </h2>
          <p className="text-zinc-600 mt-5 max-w-lg leading-relaxed">
            Live bidding, instant push for outbids and new drops from sellers
            you follow, and one-tap checkout. Same experience that'll make you
            miss work.
          </p>

          <ul className="mt-6 space-y-3 text-sm">
            <li className="flex items-start gap-2.5">
              <BellIcon size={18} weight="fill" className="text-amber-500 mt-0.5 flex-shrink-0" />
              <span className="text-zinc-700">
                <b>Instant push alerts</b> for outbids &amp; stream-going-live
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <LightningIcon size={18} weight="fill" className="text-emerald-500 mt-0.5 flex-shrink-0" />
              <span className="text-zinc-700">
                <b>One-tap bidding</b> — no relogging, no captchas
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <DeviceMobileIcon size={18} weight="fill" className="text-[#1D78AB] mt-0.5 flex-shrink-0" />
              <span className="text-zinc-700">
                <b>Works offline</b> — browse saved streams on your commute
              </span>
            </li>
          </ul>

          <div
            className="mt-8 flex flex-nowrap gap-2 sm:gap-3 sm:flex-wrap"
            data-testid={`home-app-badges-${os}`}
          >
            {showPlay && (
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => PLAY_STORE_URL === "#" && e.preventDefault()}
              className="flex-1 sm:flex-none min-w-0 inline-flex items-center gap-2 sm:gap-3 bg-zinc-900 hover:bg-black text-white px-3 sm:px-5 py-3 rounded-2xl transition relative"
              data-testid="home-app-play-store"
            >
              <GooglePlayColorIcon size={24} className="sm:w-7 sm:h-7 flex-shrink-0" />
              <div className="text-left min-w-0">
                <div className="text-[9px] sm:text-[10px] uppercase tracking-wider text-zinc-400 leading-none truncate">
                  {PLAY_STORE_URL === "#" ? "Coming soon to" : "Get it on"}
                </div>
                <div className="text-sm sm:text-base font-bold leading-tight truncate">
                  Google Play
                </div>
              </div>
              {PLAY_STORE_URL === "#" && (
                <span className="absolute -top-2 -right-2 text-[9px] bg-amber-400 text-zinc-900 px-1.5 py-0.5 rounded-full font-bold">
                  SOON
                </span>
              )}
            </a>
            )}
            {showAppStore && (
            <a
              href={IOS_ENABLED ? APP_STORE_URL : "#"}
              target={IOS_ENABLED ? "_blank" : undefined}
              rel="noreferrer"
              onClick={(e) => !IOS_ENABLED && e.preventDefault()}
              className={`flex-1 sm:flex-none min-w-0 inline-flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-3 rounded-2xl transition relative ${
                IOS_ENABLED
                  ? "bg-zinc-900 hover:bg-black text-white"
                  : "bg-zinc-200 text-zinc-500 cursor-not-allowed"
              }`}
              data-testid="home-app-app-store"
            >
              <AppleLogoIcon size={24} weight="fill" className="sm:w-7 sm:h-7 flex-shrink-0" />
              <div className="text-left min-w-0">
                <div className="text-[9px] sm:text-[10px] uppercase tracking-wider text-zinc-400 leading-none truncate">
                  {IOS_ENABLED ? "Download on" : "Coming soon to"}
                </div>
                <div className="text-sm sm:text-base font-bold leading-tight truncate">
                  App Store
                </div>
              </div>
              {!IOS_ENABLED && (
                <span className="absolute -top-2 -right-2 text-[9px] bg-amber-400 text-zinc-900 px-1.5 py-0.5 rounded-full font-bold">
                  SOON
                </span>
              )}
            </a>
            )}
          </div>
        </div>

        {/* Right column: phone mockup */}
        <div className="order-1 lg:order-2 flex justify-center">
          <div className="relative">
            <div className="absolute inset-0 bg-[#1D78AB]/30 blur-3xl rounded-full" />
            <div className="relative w-[260px] h-[540px] bg-zinc-900 rounded-[48px] border-[10px] border-zinc-800 shadow-2xl overflow-hidden">
              <div className="absolute top-2 left-1/2 -translate-x-1/2 h-5 w-24 bg-zinc-950 rounded-full z-10" />
              <div className="absolute inset-0 bg-gradient-to-b from-[#1D78AB] via-zinc-900 to-black flex flex-col">
                <div className="h-14 relative z-20 flex items-center justify-between gap-2 px-4 pt-5 pb-2 bg-black/30 backdrop-blur-md border-b border-white/10">
                  <div className="flex items-center gap-1.5">
                    <BidoraMark size={20} withPulse />
                    <span
                      className="font-display font-black text-white text-sm tracking-tight leading-none"
                      style={{ letterSpacing: "-0.045em" }}
                    >
                      Bidorah
                    </span>
                  </div>
                  <WaveformShimmer />
                </div>
                <div className="flex-1 bg-black flex items-center justify-center text-zinc-500 text-xs relative overflow-hidden">
                  {/* Looping auction video — the live "item on camera" */}
                  <video
                    ref={videoRef}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                    className="absolute inset-0 h-full w-full object-cover"
                    data-testid="phone-mockup-video"
                  >
                    <source src="/media/amethyst-cluster.mp4" type="video/mp4" />
                  </video>
                  {/* Dim overlay so overlay cards stay readable */}
                  <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/60 pointer-events-none" />

                  <div className="absolute top-4 left-4 right-4 flex items-center justify-between text-white text-[10px] z-10 drop-shadow-lg">
                    <span className="font-bold">🔴 LIVE · 1.2K</span>
                    <span className="font-semibold">CrystalKing</span>
                  </div>
                  <div className="absolute bottom-28 left-4 right-4 rounded-2xl bg-zinc-900/80 backdrop-blur p-3 border border-white/10 z-10">
                    <div className="text-[10px] text-zinc-400 uppercase tracking-wider">
                      Live auction
                    </div>
                    <div className="text-white font-bold text-sm mt-1">
                      Amethyst cluster · XL
                    </div>
                    <div className="text-[#4FC3F7] font-black text-xl mt-1">
                      $300
                    </div>
                  </div>
                  <div className="absolute bottom-14 left-4 right-4 bg-amber-400 text-zinc-900 text-center font-bold text-xs py-2 rounded-full z-10">
                    Awaiting next item
                  </div>
                  <div className="absolute bottom-4 left-4 right-4 flex gap-2 z-10">
                    <div className="flex-1 bg-white/10 backdrop-blur rounded-full h-9 px-3 flex items-center text-zinc-200 text-[11px]">
                      Say something…
                    </div>
                    <div className="h-9 w-9 rounded-full bg-[#1D78AB] grid place-items-center">
                      ✈️
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


/* ---------------- Waveform shimmer ---------------- */
/** 5 thin amber bars pulsing in a staggered sine — sells the "audio-on / live"
 * vibe next to the logo without dominating the screen. */
function WaveformShimmer() {
  // Staggered delays (ms) produce a walking-wave effect across the 5 bars.
  const bars = [0, 120, 60, 180, 90];
  return (
    <div
      className="flex items-end gap-[3px] h-5"
      aria-hidden="true"
      data-testid="phone-mockup-waveform"
    >
      <style>{`
        @keyframes bidorah-wave {
          0%, 100% { transform: scaleY(0.35); opacity: .7; }
          50%      { transform: scaleY(1);    opacity: 1;  }
        }
      `}</style>
      {bars.map((delay, i) => (
        <span
          key={i}
          className="w-[3px] rounded-full bg-gradient-to-t from-amber-300 to-amber-500"
          style={{
            height: `${60 + (i % 3) * 15}%`,
            transformOrigin: "bottom",
            animation: `bidorah-wave 900ms ease-in-out ${delay}ms infinite`,
          }}
        />
      ))}
      <span className="ml-1 text-[9px] font-black tracking-widest text-amber-400 uppercase">
        Live
      </span>
    </div>
  );
}
